import React, {useCallback, useEffect, useState} from 'react';
import {connect} from 'react-redux';
import * as PropTypes from 'prop-types';
import {Button, Col, Icon, Input, message, Row, Table, Tag, Typography} from 'antd';
import {FormattedMessage, useIntl} from 'react-intl';
import ConfigHeading from '../components/ConfigHeading';
import {selectVehicleDetailsPageDataRequest} from '../../../../state/ui/configuration/vehicles/vehicleUiReducer';
import {selectVehicles} from '../../../../state/domain/vehicles/vehicleReducer';
import {
  editVehicle as editVehicleAction,
  editVehicleDamage as editVehicleDamageAction,
  reinstateVehicle,
  requestAllVehicleDetails,
  requestVehicleDetailsPageData,
  retireVehicle as retireVehicleAction,
} from '../../../../state/ui/configuration/vehicles/vehicleUiActions';
import EditVehicleDamage from './EditVehicleDamage';
import EditVehicleDetail from './EditVehicleDetail';
import {selectSessionUser} from '../../../../state/ui/application/sessionUiReducer';
import {userIsAdmin} from '../company/user-util';
import RetireVehicle from './RetireVehicle';
import ReinstateVehicle from './ReinstateVehicle';
import {getDateDisplay, getDateFromComparisonNumber} from "../../bynx/packageUtil";
import {getDefaultLocale} from "../../../../configWeb";


const { Column } = Table;
const { Text } = Typography;

// SORTS
function sortVehicleDetailsByRegNumber(a, b) {
  const aStr = `${a.regNumber}`;
  const bStr = `${b.regNumber}`;
  return aStr.localeCompare(bStr);
}

function VehicleDetailsTable({
  loading, vehicleDetails, onEditButton, sessionUser, onRepairButton, onRetireButton, requestAllVehicleDetails, onReinstateButton, expandedInfoKeys, setExpandedInfoKeys
}) {
  const intl = useIntl();

  const [filteredInfo, setFilteredInfo] = useState( { active: ['true'] });
  const [allVehiclesLoaded, setAllVehiclesLoaded] = useState(false);

  // Locale adjust
  const userLocale = sessionUser && sessionUser.locale ? sessionUser.locale.replace('_', '-') : getDefaultLocale().locale;

  const RowTotal = () => (
    <div>
      <Text>{`${vehicleDetails.length} `}</Text>
      <FormattedMessage id="items" />
    </div>
  );

  function filterStatus(value, record)
  {
    return record.active ? value === "true" : value === "false";
  }

  function handleChange(pagination, filters, sorter) 
  {
    filters.active = filters.active && filters.active.length > 0 ? filters.active : ["true"];
    const includeRetired = filters.active && filters.active.includes("false");
    if (includeRetired && !allVehiclesLoaded)
    {
      requestAllVehicleDetails();
      setAllVehiclesLoaded(true)
    }
    
    setFilteredInfo(filters);
  }

  function getColumnSearchProps(dataIndex, searchBoxText) {
    let searchInput = () => {};
    const searchBoxBackGroundText = `${intl.formatMessage({ id: 'search' })} ${searchBoxText}`;

    return {
      filterDropdown: ({
        setSelectedKeys, selectedKeys, confirm, clearFilters,
      }) => (
        <div style={{ padding: 8 }}>
          <Input
            ref={(node) => { searchInput = node; }}
            placeholder={searchBoxBackGroundText}
            value={selectedKeys[0]}
            onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={confirm}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Button
            type="primary"
            onClick={confirm}
            icon="search"
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            {intl.formatMessage({ id: 'search' })}
          </Button>
          <Button onClick={clearFilters} size="small" style={{ width: 90 }}>
            Reset
          </Button>
        </div>
      ),
      filterIcon: filtered => <Icon type="search" style={{ color: filtered ? '#1890ff' : undefined }} />,
      onFilter: (value, record) => record[dataIndex].toString().toLowerCase().replace(/\s/g, '').includes(value.toLowerCase().replace(/\s/g, '')),
      onFilterDropdownVisibleChange: (visible) => {
        if (visible) {
          setTimeout(() => searchInput.select());
        }
      },
    };
  }
  // TABLE SETTINGS, COLUMNS
  return (
    <Table
      rowKey={vehicle => vehicle.vehicleId}
      loading={loading}
      pagination={{ defaultPageSize: 10, showTotal: () => <RowTotal /> }}
      scroll={{ x: 1, y: false }}
      dataSource={vehicleDetails}
      onChange={handleChange}
      expandedRowKeys={expandedInfoKeys}
      onExpand={(expanded, record) => {
          // need to do this as we have implemented expanded row control so that the reinstate can clear the expanded row (usually done for you)
          setExpandedInfoKeys(currentKeys => {
            let newKeys = [];
            if (expanded) {
              newKeys = currentKeys.slice()
              newKeys.push(record.vehicleId);
            } else {
              currentKeys.forEach(key => {
                if (key !== record.vehicleId) {
                  newKeys.push(key);
                }
              });
            }
            return newKeys;
          });
      }}
      expandIcon={({ expanded, record, onExpand }) => (
        record.retiredBy && record.retiredBy > -1
        ? <Icon
              className="via-config-expand-icon-info"
              theme="filled"
              type={expanded ? 'caret-down' : 'info-circle'}
              onClick={e => onExpand(record, e)}
          />
        : <></>
      )}
      expandIconAsCell={false}
      expandIconColumnIndex={0}
      expandedRowRender={(record) => {
        const displayDate = getDateDisplay(getDateFromComparisonNumber(record.retiredOn), userLocale);
        return (
          (record.retiredBy && record.retiredBy > -1)
          ? <Row type="flex" justify="start">
              <Col span={1}></Col>
              <Col span={23}>
                <FormattedMessage id="retired"/>&nbsp;<FormattedMessage id="by"/>&nbsp;
                <Text>{record.retiredByUsername}</Text>&nbsp;<FormattedMessage id="on"/>&nbsp;
                <Text>{displayDate}</Text>
              </Col>
            </Row>
          : <></>
        )
      }}
    >
      <Column
        title={<FormattedMessage id="status" />}
        dataIndex="active"
        key="active"
        width={120}
        align="center"
        filtered
        filteredValue={filteredInfo.active}
        filters={[{
          text: <FormattedMessage id="active" />,
          value: "true",
        }, {          
          text: <FormattedMessage id="retired" />,
          value: "false",
        }]}
        onFilter={filterStatus}
        render={(active, record) => {
          return (
            record.active ? 
            <Tag color="green" key="true">
              {<FormattedMessage id="active" />}
            </Tag>
            :            
            <Tag color="red" key="false">
              {<FormattedMessage id="retired" />}
            </Tag>
          );
        }}
      />
      <Column
        defaultSortOrder="ascend"
        sorter={sortVehicleDetailsByRegNumber}
        title={<FormattedMessage id="regNumber" />}
        dataIndex="regNumber"
        key="regNumber"
        width="20%"
        {...getColumnSearchProps('regNumber', intl.formatMessage({ id: 'regNumber' }))}
      />
      <Column
        title={<FormattedMessage id="vehicleDescription" />}
        dataIndex="vehicleDescription"
        key="vehicleDescription"
        width="35%"
      />
      <Column
        title={<FormattedMessage id="vehicleDetailsOdo" />}
        dataIndex="lastOdoReading"
        key="lastOdoReading"
        align="right"
        width="15%"
      />
      { userIsAdmin(sessionUser)
        ? (
          <Column
            className="via-action-column"
            align="center"
            width={220}
            render={(text, record) => (
              record.active ? 
              <div className="button-flex-col">
                <Button size="small" type="Default" disabled={!record.isVehicleDamaged} onClick={() => onRepairButton(record)}>
                  <FormattedMessage id="repair" />
                </Button>
                <Button size="small" type="primary" onClick={() => onEditButton(record)} icon="edit">
                  <FormattedMessage id="edit" />
                </Button>
                <Button size="small" type="danger" onClick={() => onRetireButton(record)} icon="">
                  <FormattedMessage id="retire" />
                </Button>
              </div>
              :
              <div className="button-flex-col">
                <Button size="small" type="primary" onClick={() => onReinstateButton(record)} icon="">
                  <FormattedMessage id="reinstate" />
                </Button>
              </div>
            )}
          />
        ) : ''
      }
    </Table>
  );
}

VehicleDetailsTable.propTypes = {
  loading: PropTypes.bool.isRequired,
  onEditButton: PropTypes.func.isRequired,
  onRepairButton: PropTypes.func.isRequired,
  onRetireButton: PropTypes.func.isRequired,
  onReinstateButton: PropTypes.func.isRequired,
  vehicleDetails: PropTypes.arrayOf(
    PropTypes.shape({
      regNumber: PropTypes.oneOfType([PropTypes.string]).isRequired,
      vehicleDescription: PropTypes.string.isRequired,
    }).isRequired,
  ),
  sessionUser: PropTypes.shape({
    linkedRoles: PropTypes.arrayOf(PropTypes.string).isRequired,
  }).isRequired,
  setSelectedKeys: PropTypes.string,
  selectedKeys: PropTypes.string,
  confirm: PropTypes.string,
  clearFilters: PropTypes.string,
};

VehicleDetailsTable.defaultProps = {
  vehicleDetails: {},
  setSelectedKeys: null,
  selectedKeys: null,
  confirm: null,
  clearFilters: null,
};

// MAIN COMPONENT
function VehicleDetails({
  error, isLoading, vehicles, allVehicles, editVehicle, sessionUser, loadPageData, editVehicleDamage, retireVehicle, requestAllVehicleDetails, reinstateVehicle
}) {
  const [expandedInfoKeys, setExpandedInfoKeys] = useState([]);

  function hideReinstatedInfoRow(vehicleId) {
    setExpandedInfoKeys(currentKeys => {
      const newKeys = [];
      expandedInfoKeys.forEach(key => {
        if (key !== vehicleId) {
          newKeys.push(key);
        }
      });
      return newKeys;
    });
  }

  // Load vehicles on first entry.
  // These only need to be loaded once.
  useEffect(() => {
    loadPageData();
  }, [loadPageData]);

  // Display error messages from loading.
  useEffect(() => {
    if (error) message.error(error);
  }, [error]);

  // Extra validation helper - check if reg exists in all active (non retired) vehicles
  const doesVehicleExist = useCallback(value => {
    const valueReg = (value || '').toString().toLowerCase().replace(/\s/g, '');
    return (allVehicles.some(row => {
     const rowReg = row.regNumber.toString().toLowerCase().replace(/\s/g, '');
     return row.active && rowReg === valueReg;
   }));
  }, [allVehicles]);

  // TITLE ROW
  const CreateButton = () => (
    <Button type="primary" onClick={() => editVehicle(null)} loading={isLoading} icon="plus">
      <FormattedMessage id="create" />
    </Button>
  );

  return (
    <ConfigHeading
      titleId="vehicleManagement"
      descriptionId="vehicleManagementDesc"
      actions={(
        userIsAdmin(sessionUser)
          ? <CreateButton />
          : <></>
      )}
    >
      <VehicleDetailsTable
        loading={isLoading}
        vehicleDetails={vehicles}
        onEditButton={editVehicle}
        sessionUser={sessionUser}
        onRepairButton={editVehicleDamage}
        onRetireButton={retireVehicle}
        onReinstateButton={reinstateVehicle}
        requestAllVehicleDetails={requestAllVehicleDetails}
        expandedInfoKeys={expandedInfoKeys}
        setExpandedInfoKeys={setExpandedInfoKeys}
      />
      <EditVehicleDetail descValidator={doesVehicleExist}  />
      <EditVehicleDamage />
      <RetireVehicle />
      <ReinstateVehicle hideReinstatedInfoRow={hideReinstatedInfoRow} />
    </ConfigHeading>
  );
}

/**
 * Validation for the props on the component.
 */
VehicleDetails.propTypes = {
  isLoading: PropTypes.bool,
  error: PropTypes.string,
  loadPageData: PropTypes.func.isRequired,
  editVehicle: PropTypes.func.isRequired,
  editVehicleDamage: PropTypes.func.isRequired,
  retireVehicle: PropTypes.func.isRequired,
  requestAllVehicleDetails: PropTypes.func.isRequired,
  vehicles: PropTypes.arrayOf(
    PropTypes.shape({
      regNumber: PropTypes.oneOfType([PropTypes.string]).isRequired,
      vehicleDescription: PropTypes.string.isRequired,
    }).isRequired,
  ),
  allVehicles: PropTypes.arrayOf(
    PropTypes.shape({
      regNumber: PropTypes.oneOfType([PropTypes.string]).isRequired,
      vehicleDescription: PropTypes.string.isRequired,
    }).isRequired,
  ),
  sessionUser: PropTypes.shape({
    linkedRoles: PropTypes.arrayOf(PropTypes.string).isRequired,
  }).isRequired,
};

/**
 * Default data for the component if nothing has been supplied.
 * Only needed for non-required props.
 */
VehicleDetails.defaultProps = {
  isLoading: false,
  error: null,
  vehicles: null,
  allVehicles: null,
};

/**
 * A function for connecting redux state to the component props.
 */
const mapStateToProps = (state) => {
  const pageDataRequest = selectVehicleDetailsPageDataRequest(state);
  const vehicleDetails = selectVehicles(state);

  return {
    isLoading: pageDataRequest ? pageDataRequest.isLoading : false,
    error: pageDataRequest ? pageDataRequest.error : null,
    vehicles: vehicleDetails,
    allVehicles: vehicleDetails || [],
    sessionUser: selectSessionUser(state),
  };
};

/**
 * A function for connecting redux action to the component props.
 */
const mapDispatchToProps = dispatch => ({
  loadPageData: () => dispatch(requestVehicleDetailsPageData()),
  editVehicle: vehicle => dispatch(editVehicleAction(vehicle ? vehicle.vehicleId : null)),
  editVehicleDamage: vehicle => dispatch(editVehicleDamageAction(vehicle ? vehicle.vehicleId : null)),
  retireVehicle: vehicle => dispatch(retireVehicleAction(vehicle ? vehicle.vehicleId : null)),
  requestAllVehicleDetails: () => dispatch(requestAllVehicleDetails()),
  reinstateVehicle: vehicle => dispatch(reinstateVehicle(vehicle ? vehicle.vehicleId : null)),
});

/**
 * Connect the redux state to our component.
 */
export default connect(mapStateToProps, mapDispatchToProps)(VehicleDetails);
